import { useEffect, useState, type ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchJauges, deleteJauge } from '@/store/thunks/jaugeThunks';
import type { Jauge } from '@/types';
import { checkPermission } from '@/utils/checkPermission';
import Page from '@/components/Page';
import { AddButton, FilterInput, Modal, DataTable, ConfirmModal, ListWithPagination, TableActions } from '@/components/ui';
import JaugerForm from '@/components/citerne/JaugerForm';

const dateRender = (v: unknown) => (v ? new Date(v as string).toLocaleDateString('fr-FR') : '—');

function citerneInfo(v: unknown): ReactNode {
  if (v == null || typeof v !== 'object') return '—';
  const o = v as { code?: string; capacite?: number; current_value?: number };
  const code = o.code ?? '—';
  const parts = [code];
  if (o.capacite != null) parts.push(`Cap. ${o.capacite}`);
  if (o.current_value != null) parts.push(`Stock ${o.current_value}`);
  return <span className="whitespace-nowrap">{parts.join(' · ')}</span>;
}

function userInfo(v: unknown): ReactNode {
  if (v == null || typeof v !== 'object') return '—';
  const o = v as { nom?: string; code?: string; prenom?: string };
  const name = [o.prenom, o.nom].filter(Boolean).join(' ') || (o.nom ?? o.code ?? '—');
  const extra = o.code && o.code !== name ? o.code : null;
  return <span className="whitespace-nowrap">{extra ? `${name} (${extra})` : name}</span>;
}

function pompisteInfo(v: unknown): ReactNode {
  if (v == null || typeof v !== 'object') return '—';
  const o = v as { nom?: string; prenom?: string };
  const name = [o.prenom, o.nom].filter(Boolean).join(' ') || (o.nom ?? '—');
  return <span className="whitespace-nowrap">{name}</span>;
}

export default function JaugePage() {
  const dispatch = useAppDispatch();
  const { jauges, pageNum, totalPages } = useAppSelector((s: RootState) => s.jauge);
  const { isAdmin, authorities } = useAppSelector((s: RootState) => s.auth);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<Jauge | null>(null);
  const [toDelete, setToDelete] = useState<Jauge | null>(null);
  const [filterQuery, setFilterQuery] = useState('');

  useEffect(() => {
    dispatch(fetchJauges(0));
  }, [dispatch]);

  const canCreate = isAdmin || checkPermission('CREATE_JAUGES', authorities);
  const canUpdate = isAdmin || checkPermission('UPDATE_JAUGES', authorities);
  const canDelete = isAdmin || checkPermission('DELETE_JAUGES', authorities);

  const columns = [
    { key: 'id', label: 'Id' },
    { key: 'date', label: 'Date', render: dateRender },
    { key: 'valeur', label: 'Valeur' },
    { key: 'temperateure', label: 'Température' },
    { key: 'citerne', label: 'Citerne', render: (_: unknown, row: Record<string, unknown>) => citerneInfo(row.citerne) },
    { key: 'user', label: 'Utilisateur', render: (_: unknown, row: Record<string, unknown>) => userInfo(row.user) },
    { key: 'pompiste', label: 'Pompiste', render: (_: unknown, row: Record<string, unknown>) => pompisteInfo(row.pompiste) },
  ];

  const tableDataRaw = jauges as Record<string, unknown>[];
  const filterLower = filterQuery.trim().toLowerCase();
  const tableData = !filterLower
    ? tableDataRaw
    : tableDataRaw.filter(
        (row) =>
          String(row.valeur ?? '').toLowerCase().includes(filterLower) ||
          String((row.citerne as { code?: string })?.code ?? '').toLowerCase().includes(filterLower) ||
          String((row.user as { nom?: string; login?: string })?.nom ?? '').toLowerCase().includes(filterLower) ||
          String((row.user as { login?: string })?.login ?? '').toLowerCase().includes(filterLower)
      );

  return (
    <Page title="Jauges">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">Liste des jauges</h2>
            <FilterInput value={filterQuery} onChange={setFilterQuery} placeholder="Rechercher…" />
          </div>
          <AddButton label="Nouvelle jauge" disabled={!canCreate} onClick={() => { setEditing(null); setModalOpen(true); }} />
        </div>
        <ListWithPagination
          page={pageNum}
          totalPages={totalPages}
          onPageChange={(p) => dispatch(fetchJauges(p))}
        >
          <DataTable<Record<string, unknown>>
            columns={columns}
            data={tableData}
            keyField="id"
            emptyMessage="Aucune jauge"
            actions={(row) => (
              <TableActions
                onEdit={() => { setEditing(row as Jauge); setModalOpen(true); }}
                onDelete={() => { setToDelete(row as Jauge); setConfirmOpen(true); }}
                editDisabled={!canUpdate}
                deleteDisabled={!canDelete}
              />
            )}
          />
        </ListWithPagination>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Modifier la jauge' : 'Nouvelle jauge'}>
        <JaugerForm jauge={editing ?? undefined} onClose={() => setModalOpen(false)} onSuccess={() => { setModalOpen(false); dispatch(fetchJauges(0)); }} />
      </Modal>

      <ConfirmModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={() => { if (toDelete) { dispatch(deleteJauge(toDelete)); setToDelete(null); setConfirmOpen(false); } }} message="Voulez-vous supprimer cette jauge ?" />
    </Page>
  );
}
